import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Sparkles } from 'lucide-react';

const insights = [
  'Gathering estimated budget to win',
  'Analyzing location swing voter key issues',
  'Calculating comparative campaign analysis',
  'Optimizing fundraising & spending strategy',
  'Developing media field & outreach strategy'
];

interface LoadingScreenProps {
  onComplete?: () => void;
}

export function LoadingScreen({ onComplete }: LoadingScreenProps) {
  const [currentInsight, setCurrentInsight] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentInsight((prev) => (prev + 1) % insights.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 bg-[#0a1229]/90 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="max-w-2xl w-full mx-4">
        <div className="text-center space-y-8">
          {/* Logo and Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center gap-6"
          >
            <div className="relative">
              {/* Outer Glow Ring */}
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 rounded-full bg-[#026fb9]/20 filter blur-xl"
              />
              
              {/* Inner Pulse Ring */}
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  opacity: [1, 0.8, 1]
                }}
                transition={{ 
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 rounded-full bg-[#026fb9]/30"
              />
              
              {/* Icon Container */}
              <div className="relative z-10 p-6 rounded-full bg-[#1a2236] border border-[#026fb9]/30">
                <Brain className="w-12 h-12 text-[#026fb9]" />
              </div>
            </div>

            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Sutton Smart Insights
              </h2>
              <div className="flex items-center justify-center gap-2 text-[#026fb9]">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">AI-Powered Campaign Analysis</span>
                <Sparkles className="w-4 h-4" />
              </div>
            </div>
          </motion.div>

          {/* Progress Bar */}
          <div className="relative h-1.5 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#026fb9] via-[#00a3ff] to-[#026fb9] rounded-full"
              initial={{ x: '-100%' }}
              animate={{ 
                x: '100%',
                transition: { 
                  duration: 2,
                  repeat: Infinity,
                  ease: "linear"
                }
              }}
            />
          </div>

          {/* Animated Text */}
          <div className="h-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentInsight}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="text-white/80"
              >
                {insights[currentInsight]}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Animated Dots */}
          <div className="flex justify-center gap-2">
            {insights.map((_, index) => (
              <motion.div
                key={index}
                animate={{
                  scale: currentInsight === index ? 1.2 : 1,
                  opacity: currentInsight === index ? 1 : 0.3
                }}
                className={`w-2 h-2 rounded-full ${
                  currentInsight === index ? 'bg-[#026fb9]' : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}